import React, { useState, useEffect } from 'react';
import { Box, Heading, Text, Select, Flex, Divider, Input, useToast, Button, Table, Thead, Tbody, Tr, Th, Td, Progress, Menu, MenuButton, MenuList, MenuItem } from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon, ChevronDownIcon } from '@chakra-ui/icons';
import { useAuth } from '../hooks/useAuth';
import { api } from '../services/api';

const meses = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

function formatarMoedaInput(valor: string | number) {
  // Se for número, converte para string de centavos
  let v = '';
  if (typeof valor === 'number') {
    v = Math.round(valor * 100).toString();
  } else {
    v = valor.replace(/\D/g, '');
  }
  if (!v) return '0,00';
  // Garante ao menos 3 dígitos para separar centavos (0.05 → '5' → '0,05')
  while (v.length < 3) v = '0' + v;
  // Separa centavos
  const centavos = v.slice(-2);
  let reais = v.slice(0, -2);
  reais = reais.replace(/^0+/, '') || '0';
  reais = reais.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${reais},${centavos}`;
}

function moedaParaNumero(valor: string) {
  return Number(valor.replace(/\./g, '').replace(',', '.'));
}

const fmt = (v: number) => v.toLocaleString('pt-BR', { minimumFractionDigits: 2 });

const Planejamento: React.FC = () => {
  const { user } = useAuth();
  const hoje = new Date();
  const [mes, setMes] = useState(hoje.getMonth());
  const [ano, setAno] = useState(hoje.getFullYear());
  const [categorias, setCategorias] = useState<any[]>([]);
  const [planejamentos, setPlanejamentos] = useState<any[]>([]);
  const [valores, setValores] = useState<{ [catId: string]: string }>({});
  const [categoriasOcultas, setCategoriasOcultas] = useState<string[]>([]);
  const [realizados, setRealizados] = useState<{ [catId: string]: number }>({});
  const toast = useToast();

  useEffect(() => {
    if (!user) return;
    const fetchCategorias = async () => {
      try {
        setCategorias(await api.get('/api/categorias'));
      } catch {
        setCategorias([]);
      }
    };
    fetchCategorias();
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const fetchPlanejamentos = async () => {
      try {
        const data = await api.get(`/api/planejamentos?mes=${mes}&ano=${ano}`);
        setPlanejamentos(data);
        // Preencher valores locais
        const novosValores: { [catId: string]: string } = {};
        data.forEach((p: any) => {
          novosValores[p.categoria_id] = formatarMoedaInput(p.valor_planejado || 0);
        });
        setValores(novosValores);
      } catch {
        setPlanejamentos([]);
        setValores({});
      }
    };
    fetchPlanejamentos();
  }, [user, mes, ano]);

  // Realizado do mês por categoria (soma dos lançamentos). A chave inclui o
  // tipo do lançamento para uma receita lançada numa categoria de despesa
  // (ex: estorno) não inflar o "gasto" — mesma regra do Dashboard.
  useEffect(() => {
    if (!user) return;
    const fetchRealizados = async () => {
      let data: any[] = [];
      try {
        data = await api.get(`/api/lancamentos?mes=${mes}&ano=${ano}`);
      } catch {
        data = [];
      }
      const mapa: { [chave: string]: number } = {};
      data.forEach(l => {
        if (!l.categoria_id) return;
        const chave = `${l.categoria_id}|${l.tipo_lancamento}`;
        mapa[chave] = (mapa[chave] || 0) + Number(l.valor);
      });
      setRealizados(mapa);
    };
    fetchRealizados();
  }, [user, mes, ano]);

  // Buscar categorias ocultas
  useEffect(() => {
    if (!user) return;
    const fetchCategoriasOcultas = async () => {
      try {
        const data = await api.get(`/api/categorias-ocultas?mes=${mes}&ano=${ano}`);
        setCategoriasOcultas(data.map((d: any) => d.categoria_id));
      } catch {
        setCategoriasOcultas([]);
      }
    };
    fetchCategoriasOcultas();
  }, [user, mes, ano]);

  // Atualizar valor localmente com máscara
  const handleValorChange = (catId: string, valor: string) => {
    setValores(v => ({ ...v, [catId]: formatarMoedaInput(valor) }));
  };

  // Salvar valor planejado ao sair do campo ou pressionar Enter
  const handleValorSave = async (catId: string) => {
    if (!user) return;
    const valorStr = valores[catId] || '0,00';
    const valorNum = moedaParaNumero(valorStr);
    if (isNaN(valorNum) || valorNum < 0) {
      toast({ title: 'Valor inválido', status: 'error' });
      return;
    }
    // Evita requisição quando nada mudou (onBlur dispara mesmo sem edição)
    const existente = planejamentos.find(p => p.categoria_id === catId);
    if (existente && Number(existente.valor_planejado) === valorNum) return;
    if (!existente && valorNum === 0) return;
    try {
      // Upsert no servidor: cria ou atualiza numa chamada só
      const salvo = await api.put('/api/planejamentos', {
        categoria_id: catId,
        mes,
        ano,
        valor_planejado: valorNum,
      });
      setPlanejamentos(existente
        ? planejamentos.map(p => (p.categoria_id === catId ? { ...p, valor_planejado: valorNum } : p))
        : [...planejamentos, salvo]);
      toast({ title: 'Valor planejado salvo!', status: 'success' });
    } catch {
      toast({ title: 'Erro ao salvar valor', status: 'error' });
    }
  };

  // Salvar ao pressionar Enter
  const handleValorKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, catId: string) => {
    if (e.key === 'Enter') {
      (e.target as HTMLInputElement).blur();
      handleValorSave(catId);
    }
  };

  // Função para ocultar/mostrar categoria
  const toggleCategoriaOculta = async (categoriaId: string) => {
    if (!user) return;
    const isOculta = categoriasOcultas.includes(categoriaId);
    try {
      if (isOculta) {
        await api.delete(`/api/categorias-ocultas?categoria_id=${categoriaId}&mes=${mes}&ano=${ano}`);
        setCategoriasOcultas(categoriasOcultas.filter(id => id !== categoriaId));
        toast({ title: 'Categoria mostrada!', status: 'success' });
      } else {
        await api.post('/api/categorias-ocultas', { categoria_id: categoriaId, mes, ano });
        setCategoriasOcultas([...categoriasOcultas, categoriaId]);
        toast({ title: 'Categoria ocultada!', status: 'success' });
      }
    } catch {
      toast({ title: 'Erro ao atualizar categoria', status: 'error' });
    }
  };

  // Separar categorias (considerando ocultas do mês e inativas)
  const categoriasAtivas = categorias.filter(cat => cat.ativo !== false);
  const categoriasReceita = categoriasAtivas.filter(cat =>
    cat.tipo_lancamento === 'receita' &&
    !categoriasOcultas.includes(cat.id)
  );
  const categoriasEssenciais = categoriasAtivas.filter(cat =>
    cat.tipo_lancamento === 'despesa' &&
    cat.tipo === 'essencial' &&
    !categoriasOcultas.includes(cat.id)
  );
  const categoriasSuperfluas = categoriasAtivas.filter(cat =>
    cat.tipo_lancamento === 'despesa' &&
    cat.tipo === 'superfluo' &&
    !categoriasOcultas.includes(cat.id)
  );
  const categoriasInvestimento = categoriasAtivas.filter(cat =>
    cat.tipo_lancamento === 'despesa' &&
    cat.tipo === 'investimento' &&
    !categoriasOcultas.includes(cat.id)
  );

  // Totais
  const totalReceita = categoriasReceita.reduce((acc, cat) => acc + moedaParaNumero(valores[cat.id] || '0,00'), 0);
  const totalEssenciais = categoriasEssenciais.reduce((acc, cat) => acc + moedaParaNumero(valores[cat.id] || '0,00'), 0);
  const totalSuperfluas = categoriasSuperfluas.reduce((acc, cat) => acc + moedaParaNumero(valores[cat.id] || '0,00'), 0);
  const totalInvestimento = categoriasInvestimento.reduce((acc, cat) => acc + moedaParaNumero(valores[cat.id] || '0,00'), 0);

  // Limites
  const limiteEssenciais = totalReceita * 0.5;
  const limiteSuperfluas = totalReceita * 0.3;
  const limiteInvestimento = totalReceita * 0.2;

  // Fechamento do plano: total planejado × receita planejada. Sobra positiva
  // = receita ainda sem destino; negativa = o plano estoura a receita
  const totalPlanejado = totalEssenciais + totalSuperfluas + totalInvestimento;
  const sobraPlano = totalReceita - totalPlanejado;

  // Realizado somado por grupo de categorias
  const realizadoDaCategoria = (cat: any) => realizados[`${cat.id}|${cat.tipo_lancamento}`] || 0;
  const realizadoDe = (cats: any[]) => cats.reduce((acc, c) => acc + realizadoDaCategoria(c), 0);
  const realizadoReceita = realizadoDe(categoriasReceita);
  const realizadoEssenciais = realizadoDe(categoriasEssenciais);
  const realizadoSuperfluas = realizadoDe(categoriasSuperfluas);
  const realizadoInvestimento = realizadoDe(categoriasInvestimento);

  // Função para cor do progresso
  function corProgresso(valor: number, limite: number) {
    if (valor > limite) return 'red';
    if (valor > limite * 0.9) return 'orange';
    return 'teal';
  }

  // Célula "Realizado" das tabelas: valor gasto/recebido no mês + % do planejado.
  // Para despesas, alerta em laranja a partir de 80% e vermelho acima de 100%.
  const renderRealizado = (cat: any) => {
    const planejado = moedaParaNumero(valores[cat.id] ?? '0,00');
    const realizado = realizadoDaCategoria(cat);
    const pct = planejado > 0 ? (realizado / planejado) * 100 : 0;
    const isDespesa = cat.tipo_lancamento === 'despesa';
    const cor = isDespesa ? (pct > 100 ? 'red' : pct >= 80 ? 'orange' : 'teal') : 'teal';
    return (
      <Box minW={{ base: '104px', md: '140px' }} display="inline-block">
        <Text fontSize="sm" textAlign="right" color={isDespesa && pct > 100 ? 'red.500' : undefined}>
          R$ {realizado.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
          {planejado > 0 ? ` (${pct.toFixed(0)}%)` : ''}
        </Text>
        {planejado > 0 && (
          <Progress value={Math.min(pct, 100)} colorScheme={cor} size="xs" borderRadius="md" mt={1} />
        )}
      </Box>
    );
  };

  return (
    <Box p={4} maxW="800px" mx="auto">
      <Heading size="lg" mb={2} textAlign="center">
        Olá, {user?.nome || 'Usuário'}!
      </Heading>
      <Text textAlign="center" mb={6}>Planejamento financeiro de {meses[mes]} de {ano}</Text>
      <Flex justify="center" mb={6} gap={2}>
        <Select value={mes} onChange={e => setMes(Number(e.target.value))} w="auto">
          {meses.map((m, idx) => (
            <option value={idx} key={m}>{m}</option>
          ))}
        </Select>
        <Select value={ano} onChange={e => setAno(Number(e.target.value))} w="auto">
          {[ano-1, ano, ano+1].map(a => (
            <option value={a} key={a}>{a}</option>
          ))}
        </Select>
      </Flex>
      <Divider mb={4} />
      {/* Resumo do plano: uma linha por bloco (planejado × limite) e o
          fechamento (total + sobra) no rodapé */}
      <Box mb={8} p={4} borderRadius="md" boxShadow="md" bg="white" borderWidth="1px">
        <Flex justify="space-between" align="baseline" mb={4} direction={{ base: 'column', md: 'row' }} gap={1}>
          <Heading size="sm">Plano de {meses[mes]}</Heading>
          <Text fontSize="sm" color="gray.600">
            Receita planejada: <Text as="span" fontWeight="bold" color="gray.800">R$ {fmt(totalReceita)}</Text>
            {' '}· recebido R$ {fmt(realizadoReceita)}
          </Text>
        </Flex>

        {[
          { rotulo: '🏠 Essenciais', regra: 'até 50%', planejado: totalEssenciais, limite: limiteEssenciais, gasto: realizadoEssenciais, minimo: false },
          { rotulo: '🎁 Supérfluos', regra: 'até 30%', planejado: totalSuperfluas, limite: limiteSuperfluas, gasto: realizadoSuperfluas, minimo: false },
          { rotulo: '📈 Investimentos', regra: 'mín. 20%', planejado: totalInvestimento, limite: limiteInvestimento, gasto: realizadoInvestimento, minimo: true },
        ].map(b => (
          <Box key={b.rotulo} mb={3}>
            <Flex justify="space-between" fontSize="sm" mb={1} wrap="wrap" columnGap={3}>
              <Text fontWeight="bold">
                {b.rotulo}{' '}
                <Text as="span" fontWeight="normal" color="gray.500">({b.regra}: R$ {fmt(b.limite)})</Text>
              </Text>
              <Text>
                <Text as="span" fontWeight="bold">R$ {fmt(b.planejado)}</Text>
                <Text as="span" color="gray.500"> planejado · gasto R$ {fmt(b.gasto)}</Text>
              </Text>
            </Flex>
            {/* Barra = planejado ÷ limite. Nos blocos "até X%", passar é ruim
                (laranja/vermelho); no investimento o limite é MÍNIMO, então
                atingir é verde e ficar abaixo é alerta */}
            <Progress
              value={Math.min((b.planejado / (b.limite || 1)) * 100, 100)}
              colorScheme={b.minimo
                ? (b.planejado >= b.limite ? 'green' : 'orange')
                : corProgresso(b.planejado, b.limite)}
              size="sm"
              borderRadius="md"
            />
          </Box>
        ))}

        <Divider my={3} />
        <Flex justify="space-between" wrap="wrap" columnGap={4} rowGap={1} align="baseline">
          <Text>
            Total planejado: <Text as="span" fontWeight="bold">R$ {fmt(totalPlanejado)}</Text>
            <Text as="span" color="gray.500"> ({((totalPlanejado / (totalReceita || 1)) * 100).toFixed(1)}% da receita)</Text>
          </Text>
          <Text fontWeight="bold" color={sobraPlano < 0 ? 'red.500' : 'green.600'}>
            {sobraPlano >= 0 ? '✓ Sobra pra distribuir' : '⚠ Plano estourado'}: {sobraPlano < 0 ? '-' : ''}R$ {fmt(Math.abs(sobraPlano))}
          </Text>
        </Flex>
      </Box>
      <Box>
        {/* Tabela Receitas */}
        <Box mb={8}>
          <Flex justify="space-between" align="center" gap={2} wrap="wrap" mb={2}>
            <Heading size="md">Receitas</Heading>
            {/* isLazy: fechado não monta a lista — nome de categoria longo não empurra a largura da página no celular */}
            <Menu isLazy>
              <MenuButton as={Button} rightIcon={<ChevronDownIcon />} size="sm">
                Gerenciar categorias
              </MenuButton>
              <MenuList maxW="calc(100vw - 2rem)" maxH="60vh" overflowY="auto">
                {categoriasAtivas.filter(cat => cat.tipo_lancamento === 'receita').map(cat => (
                  <MenuItem 
                    key={cat.id} 
                    icon={categoriasOcultas.includes(cat.id) ? <ViewOffIcon /> : <ViewIcon />}
                    onClick={() => toggleCategoriaOculta(cat.id)}
                  >
                    {cat.nome} ({categoriasOcultas.includes(cat.id) ? 'Oculta' : 'Visível'})
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
          </Flex>
          <Box overflowX="auto" maxW="100%">
            {/* Tabela larga rola dentro da própria caixa: no celular a página inteira não desliza pro lado */}
            <Table size="sm" variant="simple" sx={{ 'th, td': { px: { base: 1, md: 4 } } }}>
              <Thead>
                <Tr>
                  <Th>Categoria</Th>
                  <Th isNumeric>Planejado</Th>
                  <Th isNumeric>Realizado</Th>
                </Tr>
              </Thead>
              <Tbody>
                {categoriasReceita.map(cat => (
                  <Tr key={cat.id}>
                    <Td>{cat.nome}</Td>
                    <Td isNumeric>
                      <Input
                        type="text"
                        value={valores[cat.id] ?? '0,00'}
                        onChange={e => handleValorChange(cat.id, e.target.value)}
                        onBlur={() => handleValorSave(cat.id)}
                        onKeyDown={e => handleValorKeyDown(e, cat.id)}
                        placeholder="0,00"
                        inputMode="decimal"
                        maxLength={17}
                        w={{ base: '100px', md: '120px' }}
                        px={{ base: 2, md: 4 }}
                        textAlign="right"
                      />
                    </Td>
                    <Td isNumeric>{renderRealizado(cat)}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </Box>

        {/* Tabela Essenciais */}
        <Box mb={8}>
          <Flex justify="space-between" align="center" gap={2} wrap="wrap" mb={2}>
            <Heading size="md">Despesas Essenciais</Heading>
            {/* isLazy: fechado não monta a lista — nome de categoria longo não empurra a largura da página no celular */}
            <Menu isLazy>
              <MenuButton as={Button} rightIcon={<ChevronDownIcon />} size="sm">
                Gerenciar categorias
              </MenuButton>
              <MenuList maxW="calc(100vw - 2rem)" maxH="60vh" overflowY="auto">
                {categoriasAtivas.filter(cat => cat.tipo_lancamento === 'despesa' && cat.tipo === 'essencial').map(cat => (
                  <MenuItem 
                    key={cat.id} 
                    icon={categoriasOcultas.includes(cat.id) ? <ViewOffIcon /> : <ViewIcon />}
                    onClick={() => toggleCategoriaOculta(cat.id)}
                  >
                    {cat.nome} ({categoriasOcultas.includes(cat.id) ? 'Oculta' : 'Visível'})
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
          </Flex>
          <Box overflowX="auto" maxW="100%">
            {/* Tabela larga rola dentro da própria caixa: no celular a página inteira não desliza pro lado */}
            <Table size="sm" variant="simple" sx={{ 'th, td': { px: { base: 1, md: 4 } } }}>
              <Thead>
                <Tr>
                  <Th>Categoria</Th>
                  <Th isNumeric>Planejado</Th>
                  <Th isNumeric>Realizado</Th>
                </Tr>
              </Thead>
              <Tbody>
                {categoriasEssenciais.map(cat => (
                  <Tr key={cat.id}>
                    <Td>{cat.nome}</Td>
                    <Td isNumeric>
                      <Input
                        type="text"
                        value={valores[cat.id] ?? '0,00'}
                        onChange={e => handleValorChange(cat.id, e.target.value)}
                        onBlur={() => handleValorSave(cat.id)}
                        onKeyDown={e => handleValorKeyDown(e, cat.id)}
                        placeholder="0,00"
                        inputMode="decimal"
                        maxLength={17}
                        w={{ base: '100px', md: '120px' }}
                        px={{ base: 2, md: 4 }}
                        textAlign="right"
                      />
                    </Td>
                    <Td isNumeric>{renderRealizado(cat)}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </Box>

        {/* Tabela Supérfluos */}
        <Box mb={8}>
          <Flex justify="space-between" align="center" gap={2} wrap="wrap" mb={2}>
            <Heading size="md">Despesas Supérfluas</Heading>
            {/* isLazy: fechado não monta a lista — nome de categoria longo não empurra a largura da página no celular */}
            <Menu isLazy>
              <MenuButton as={Button} rightIcon={<ChevronDownIcon />} size="sm">
                Gerenciar categorias
              </MenuButton>
              <MenuList maxW="calc(100vw - 2rem)" maxH="60vh" overflowY="auto">
                {categoriasAtivas.filter(cat => cat.tipo_lancamento === 'despesa' && cat.tipo === 'superfluo').map(cat => (
                  <MenuItem 
                    key={cat.id} 
                    icon={categoriasOcultas.includes(cat.id) ? <ViewOffIcon /> : <ViewIcon />}
                    onClick={() => toggleCategoriaOculta(cat.id)}
                  >
                    {cat.nome} ({categoriasOcultas.includes(cat.id) ? 'Oculta' : 'Visível'})
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
          </Flex>
          <Box overflowX="auto" maxW="100%">
            {/* Tabela larga rola dentro da própria caixa: no celular a página inteira não desliza pro lado */}
            <Table size="sm" variant="simple" sx={{ 'th, td': { px: { base: 1, md: 4 } } }}>
              <Thead>
                <Tr>
                  <Th>Categoria</Th>
                  <Th isNumeric>Planejado</Th>
                  <Th isNumeric>Realizado</Th>
                </Tr>
              </Thead>
              <Tbody>
                {categoriasSuperfluas.map(cat => (
                  <Tr key={cat.id}>
                    <Td>{cat.nome}</Td>
                    <Td isNumeric>
                      <Input
                        type="text"
                        value={valores[cat.id] ?? '0,00'}
                        onChange={e => handleValorChange(cat.id, e.target.value)}
                        onBlur={() => handleValorSave(cat.id)}
                        onKeyDown={e => handleValorKeyDown(e, cat.id)}
                        placeholder="0,00"
                        inputMode="decimal"
                        maxLength={17}
                        w={{ base: '100px', md: '120px' }}
                        px={{ base: 2, md: 4 }}
                        textAlign="right"
                      />
                    </Td>
                    <Td isNumeric>{renderRealizado(cat)}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </Box>

        {/* Tabela Investimento */}
        <Box mb={16}>
          <Flex justify="space-between" align="center" gap={2} wrap="wrap" mb={2}>
            <Heading size="md">Despesas Investimento</Heading>
            {/* isLazy: fechado não monta a lista — nome de categoria longo não empurra a largura da página no celular */}
            <Menu isLazy>
              <MenuButton as={Button} rightIcon={<ChevronDownIcon />} size="sm">
                Gerenciar categorias
              </MenuButton>
              <MenuList maxW="calc(100vw - 2rem)" maxH="60vh" overflowY="auto">
                {categoriasAtivas.filter(cat => cat.tipo_lancamento === 'despesa' && cat.tipo === 'investimento').map(cat => (
                  <MenuItem 
                    key={cat.id} 
                    icon={categoriasOcultas.includes(cat.id) ? <ViewOffIcon /> : <ViewIcon />}
                    onClick={() => toggleCategoriaOculta(cat.id)}
                  >
                    {cat.nome} ({categoriasOcultas.includes(cat.id) ? 'Oculta' : 'Visível'})
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
          </Flex>
          <Box overflowX="auto" maxW="100%">
            {/* Tabela larga rola dentro da própria caixa: no celular a página inteira não desliza pro lado */}
            <Table size="sm" variant="simple" sx={{ 'th, td': { px: { base: 1, md: 4 } } }}>
              <Thead>
                <Tr>
                  <Th>Categoria</Th>
                  <Th isNumeric>Planejado</Th>
                  <Th isNumeric>Realizado</Th>
                </Tr>
              </Thead>
              <Tbody>
                {categoriasInvestimento.map(cat => (
                  <Tr key={cat.id}>
                    <Td>{cat.nome}</Td>
                    <Td isNumeric>
                      <Input
                        type="text"
                        value={valores[cat.id] ?? '0,00'}
                        onChange={e => handleValorChange(cat.id, e.target.value)}
                        onBlur={() => handleValorSave(cat.id)}
                        onKeyDown={e => handleValorKeyDown(e, cat.id)}
                        placeholder="0,00"
                        inputMode="decimal"
                        maxLength={17}
                        w={{ base: '100px', md: '120px' }}
                        px={{ base: 2, md: 4 }}
                        textAlign="right"
                      />
                    </Td>
                    <Td isNumeric>{renderRealizado(cat)}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Planejamento; 